import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validateOAuthState, exchangeCodeForToken } from '../services/oauth';
import { analytics } from '../utils/analytics';

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Check for OAuth errors from Google
        const errorParam = searchParams.get('error');
        if (errorParam) {
          const errorDescription = searchParams.get('error_description');
          throw new Error(errorDescription || errorParam);
        }

        // Get authorization code and state
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter');
        }

        // Validate state (CSRF protection)
        if (!validateOAuthState(state)) {
          throw new Error('Invalid state parameter - possible CSRF attack');
        }

        console.log('[OAuth] Callback received, exchanging code for token...');

        // Exchange code for access token via backend
        const { token, user } = await exchangeCodeForToken(code);

        console.log('[OAuth] Successfully authenticated:', user.email);

        // Store auth in context (which persists to localStorage)
        login(token, user);

        // Track successful authentication
        analytics.track('order_completed', { userId: user.email });

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (err) {
        console.error('[OAuth] Callback error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMessage);
        analytics.error('OAuth callback failed', err instanceof Error ? err.stack : undefined);
      } finally {
        setIsProcessing(false);
      }
    };

    processOAuthCallback();
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Authentication Failed
          </h1>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transform transition hover:scale-105 active:scale-95"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
        <div className="text-6xl mb-4 animate-bounce">☕</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {isProcessing ? 'Authenticating...' : 'Success!'}
        </h1>
        <p className="text-gray-600 mb-6">
          {isProcessing
            ? 'Please wait while we verify your Google account'
            : 'Redirecting to your dashboard...'}
        </p>
        <div className="flex justify-center">
          <svg
            className="animate-spin h-8 w-8 text-amber-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      </div>
    </div>
  );
};
